/* TREEZYN CART SYSTEM — additive enhancement over Dawn native cart mechanics */
(() => {
  if (window.__TREEZYN_CART_SYSTEM__) return;
  window.__TREEZYN_CART_SYSTEM__ = true;

  /*
    The dev theme inherited Dawn's notification setting. The Treezyn announcement
    section mounts a native <cart-drawer> before the header. This script is parsed
    before Dawn's product-form.js, so remove the legacy notification surface before
    product forms choose between notification and drawer. When the store is later
    switched to native drawer mode, no notification exists and this is a no-op.
  */
  if (document.querySelector('cart-drawer')) {
    document.querySelectorAll('cart-notification').forEach((notification) => notification.remove());
  }

  const state = {
    cart: null,
    data: null,
    syncTimer: null,
    recommendationAbort: null,
    dataAbort: null,
    bound: false,
    adding: false,
    syncPromise: null,
    syncAgain: false,
    products: new Map(),
  };

  const root = window.Shopify?.routes?.root || '/';
  const allGearUrl = '/collections/all';

  function readDataFromNode(node = document.getElementById('TZCartData')) {
    if (!node) return { items: [], sectionId: '', currency: window.Shopify?.currency?.active || 'USD', allProductsUrl: allGearUrl };
    try {
      const parsed = JSON.parse(node.textContent || '{}');
      parsed.items = Array.isArray(parsed.items) ? parsed.items : [];
      return parsed;
    } catch (error) {
      return { items: [], sectionId: '', currency: window.Shopify?.currency?.active || 'USD', allProductsUrl: allGearUrl };
    }
  }

  function dispatch(name, detail = {}) {
    document.dispatchEvent(new CustomEvent(name, { detail }));
  }

  function money(cents) {
    const amount = Number(cents || 0) / 100;
    const currency = state.data?.currency || window.Shopify?.currency?.active || 'USD';
    try {
      return new Intl.NumberFormat(undefined, { style: 'currency', currency }).format(amount);
    } catch (error) {
      return `$${amount.toFixed(2)}`;
    }
  }

  function make(tag, className, text) {
    const el = document.createElement(tag);
    if (className) el.className = className;
    if (text !== undefined && text !== null) el.textContent = text;
    return el;
  }

  function currentProductIds(cart = state.cart) {
    return new Set((cart?.items || []).map((item) => String(item.product_id)));
  }

  function dataProductIds() {
    return new Set((state.data?.items || []).map((item) => String(item.productId)));
  }

  function needsDataRefresh(cart) {
    if (!state.data?.sectionId) return false;
    const known = dataProductIds();
    return (cart?.items || []).some((item) => !known.has(String(item.product_id)));
  }

  async function refreshStructuredData() {
    if (!state.data?.sectionId) return;
    if (state.dataAbort) state.dataAbort.abort();
    state.dataAbort = new AbortController();

    try {
      const url = new URL(window.location.href);
      url.searchParams.set('section_id', state.data.sectionId);
      const response = await fetch(url.toString(), {
        headers: { Accept: 'text/html' },
        signal: state.dataAbort.signal,
      });
      if (!response.ok) return;
      const html = await response.text();
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const node = doc.getElementById('TZCartData');
      if (node) state.data = readDataFromNode(node);
    } catch (error) {
      if (error.name !== 'AbortError') console.debug('[Treezyn cart] structured data refresh unavailable');
    }
  }

  async function fetchCart() {
    const response = await fetch(`${root}cart.js`, {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
    });
    if (!response.ok) throw new Error('Cart request failed');
    return response.json();
  }

  function structuredRecommendations(cart) {
    const inCart = currentProductIds(cart);
    const seen = new Set();
    const output = [];

    for (const itemData of state.data?.items || []) {
      if (!inCart.has(String(itemData.productId))) continue;
      for (const product of itemData.recommendations || []) {
        const id = String(product.id || '');
        if (!id || inCart.has(id) || seen.has(id) || product.available === false) continue;
        seen.add(id);
        output.push({
          id,
          title: product.title || 'Treezyn Gear',
          url: product.url || allGearUrl,
          image: product.image || '',
          price: Number(product.price || 0),
          compareAt: Number(product.compareAt || 0),
          season: product.season || '',
        });
      }
    }

    return output;
  }

  function normalizeRecommendationProduct(product) {
    const featured = product.featured_image;
    let image = '';
    if (typeof featured === 'string') image = featured;
    if (featured && typeof featured === 'object') image = featured.url || featured.src || '';

    return {
      id: String(product.id || ''),
      title: product.title || 'Treezyn Gear',
      url: product.url || allGearUrl,
      image,
      price: Number(product.price || product.price_min || 0),
      compareAt: Number(product.compare_at_price || product.compare_at_price_max || 0),
      season: '',
      available: product.available !== false,
    };
  }

  async function fetchRecommendationIntent(productId, intent) {
    const url = `${root}recommendations/products.json?product_id=${encodeURIComponent(productId)}&limit=6&intent=${encodeURIComponent(intent)}`;
    const response = await fetch(url, {
      headers: { Accept: 'application/json' },
      signal: state.recommendationAbort.signal,
      credentials: 'same-origin',
    });
    if (!response.ok) return [];
    const payload = await response.json();
    return Array.isArray(payload.products) ? payload.products : [];
  }

  async function fallbackRecommendations(cart) {
    const firstProductId = cart?.items?.[0]?.product_id;
    if (!firstProductId) return { products: [], source: 'none' };

    if (state.recommendationAbort) state.recommendationAbort.abort();
    state.recommendationAbort = new AbortController();

    try {
      let products = await fetchRecommendationIntent(firstProductId, 'complementary');
      let source = 'complementary';
      if (!products.length) {
        products = await fetchRecommendationIntent(firstProductId, 'related');
        source = 'related';
      }

      const inCart = currentProductIds(cart);
      const seen = new Set();
      const normalized = [];
      for (const raw of products) {
        const product = normalizeRecommendationProduct(raw);
        if (!product.id || inCart.has(product.id) || seen.has(product.id) || !product.available) continue;
        seen.add(product.id);
        normalized.push(product);
      }
      return { products: normalized, source };
    } catch (error) {
      if (error.name !== 'AbortError') console.debug('[Treezyn cart] Shopify recommendations unavailable');
      return { products: [], source: 'none' };
    }
  }

  function recommendationCopy(source) {
    if (source === 'structured') {
      return { eyebrow: 'SYSTEM RECOMMENDATIONS', title: 'COMPLETE THE SYSTEM.' };
    }
    if (source === 'complementary') {
      return { eyebrow: 'SHOPIFY COMPLEMENTARY GEAR', title: 'FINISH THE KIT.' };
    }
    return { eyebrow: 'MORE TREEZYN GEAR', title: 'MORE GEAR TO CONSIDER.' };
  }

  function quickAddText(key) {
    return state.data?.quickAdd?.[key] || '';
  }

  async function recommendationDetails(product) {
    const url = new URL(product.url, window.location.origin);
    if (url.origin !== window.location.origin) throw new Error(quickAddText('loadError'));
    url.pathname = url.pathname.replace(/\/$/, '') + '.js';
    url.search = '';
    if (!state.products.has(url.href)) {
      const request = fetch(url.href, { credentials: 'same-origin', headers: { Accept: 'application/json' } })
        .then(async (response) => {
          if (!response.ok) throw new Error(quickAddText('loadError'));
          return response.json();
        }).catch((error) => { state.products.delete(url.href); throw error; });
      state.products.set(url.href, request);
    }
    return state.products.get(url.href);
  }

  async function addRecommendedVariant(variant, button, errorNode, product, source) {
    if (state.adding) return;
    state.adding = true;
    // Finish a reward transaction before another recommendation changes the cart.
    if (state.syncPromise) await state.syncPromise;
    button.disabled = true;
    button.textContent = quickAddText('adding');
    errorNode.textContent = '';
    let deferred;
    let added = false;
    try {
      const drawer = document.querySelector('cart-drawer');
      const sections = drawer?.getSectionsToRender().map((section) => section.id) || [];
      const LinesEvent = window.StandardEvents?.CartLinesUpdateEvent;
      if (LinesEvent) {
        deferred = LinesEvent.createPromise();
        deferred.promise.catch(() => {});
        button.dispatchEvent(new LinesEvent({ action: 'add', context: 'product', lines: [{ merchandiseId: String(variant.id), quantity: 1 }], promise: deferred.promise }));
      }
      const response = await fetch(`${root}cart/add.js`, {
        method: 'POST', credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id: variant.id, quantity: 1 }], sections, sections_url: window.location.pathname }),
      });
      const result = await response.json();
      if (!response.ok || result.status) throw new Error(result.description || quickAddText('addError'));
      added = true;
      const cart = await fetchCart();
      state.cart = cart;
      if (deferred) deferred.resolve({ cart: LinesEvent.createCartFromAjaxResponse(cart) });
      if (!drawer) { window.location.assign(`${root}cart`); return; }
      if (sections.some((id) => !result.sections?.[id])) {
        const url = new URL(window.location.href);
        url.searchParams.set('sections', sections.join(','));
        const refresh = await fetch(url.href, { credentials: 'same-origin' });
        if (!refresh.ok) throw new Error('Cart refresh failed');
        result.sections = await refresh.json();
      }
      if (sections.some((id) => !result.sections?.[id])) throw new Error('Cart sections unavailable');
      drawer.setActiveElement(document.getElementById('cart-icon-bubble'));
      drawer.classList.remove('is-empty');
      drawer.renderContents(result);
      const close = drawer.querySelector('.drawer__close');
      if (typeof trapFocus === 'function') trapFocus(drawer.querySelector('.drawer__inner'), close);
      dispatch('treezyn:cart:recommendation', { productId: product.id, variantId: variant.id, source, context: 'drawer', action: 'add', quantity: 1, url: product.url });
      if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'treezyn-cart-recommendation', productVariantId: String(variant.id), cartData: result });
      }
    } catch (error) {
      deferred?.reject(error);
      if (added) { window.location.assign(`${root}cart`); return; }
      errorNode.textContent = error instanceof TypeError ? quickAddText('addError') : error.message || quickAddText('addError');
    } finally {
      state.adding = false;
      button.disabled = false;
      button.textContent = quickAddText('add');
      if (added) scheduleSync('recommendation-added');
    }
  }

  function addRecommendationControls(body, product, source, price) {
    const controls = make('div', 'tz-cart-rec-card__controls');
    const label = make('label', 'tz-cart-rec-card__option-label', quickAddText('choose'));
    const select = make('select', 'tz-cart-rec-card__select');
    select.id = `TZCartVariant-${product.id}`;
    label.htmlFor = select.id;
    const button = make('button', 'tz-cart-rec-card__add', quickAddText('loading'));
    button.type = 'button';
    button.disabled = true;
    const errorNode = make('p', 'tz-cart-rec-card__error');
    errorNode.setAttribute('role', 'alert');
    controls.append(button, errorNode);
    body.append(controls);
    const load = async () => {
      button.disabled = true;
      button.textContent = quickAddText('loading');
      errorNode.textContent = '';
      try {
        const detail = await recommendationDetails(product);
        if (!controls.isConnected) return;
        const variants = detail.variants || [];
        if (detail.requires_selling_plan) {
          button.textContent = quickAddText('chooseOnPage');
          button.disabled = false;
          button.onclick = () => window.location.assign(product.url);
          return;
        }
        let selected = variants.length === 1 && variants[0].available ? variants[0] : null;
        const updatePrice = () => {
          if (!selected) return;
          const sale = Number(selected.compare_at_price) > Number(selected.price);
          price.replaceChildren(make('span', sale ? 'tz-cart-rec-card__price--sale' : '', money(selected.price)));
          if (sale) price.append(make('span', 'tz-cart-rec-card__compare', money(selected.compare_at_price)));
        };
        if (variants.length > 1) {
          const placeholder = make('option', '', quickAddText('choose'));
          placeholder.value = '';
          select.replaceChildren(placeholder);
          variants.forEach((variant) => {
            const option = make('option', '', `${variant.title}${variant.available ? '' : ` — ${quickAddText('soldOut')}`}`);
            option.value = String(variant.id);
            option.disabled = !variant.available;
            select.append(option);
          });
          controls.prepend(label, select);
          select.addEventListener('change', () => {
            selected = variants.find((variant) => String(variant.id) === select.value && variant.available) || null;
            button.disabled = !selected;
            errorNode.textContent = '';
            updatePrice();
          });
        }
        updatePrice();
        button.textContent = variants.some((variant) => variant.available) ? quickAddText('add') : quickAddText('soldOut');
        button.disabled = !selected;
        button.onclick = () => { if (selected) addRecommendedVariant(selected, button, errorNode, product, source); };
      } catch (error) {
        if (!controls.isConnected) return;
        errorNode.textContent = quickAddText('loadError');
        button.textContent = quickAddText('retry');
        button.disabled = false;
        button.onclick = load;
      }
    };
    queueMicrotask(load);
  }

  function createRecommendationCard(product, source, context) {
    const isDrawer = context === 'drawer';
    const card = make(isDrawer ? 'article' : 'a', 'tz-cart-rec-card');
    if (!isDrawer) card.href = product.url || allGearUrl;
    card.dataset.tzCartRecommendation = 'true';
    card.dataset.productId = product.id || '';
    card.dataset.source = source;
    card.dataset.context = context;
    card.setAttribute('aria-label', `View ${product.title}`);

    const media = make(isDrawer ? 'a' : 'span', 'tz-cart-rec-card__media');
    if (isDrawer) media.href = product.url || allGearUrl;
    if (product.image) {
      const img = make('img', 'tz-cart-rec-card__image');
      img.src = product.image;
      img.alt = product.title || '';
      img.loading = 'lazy';
      img.width = 320;
      img.height = 320;
      media.appendChild(img);
    }

    const body = make(isDrawer ? 'div' : 'span', 'tz-cart-rec-card__body');
    body.appendChild(make('span', 'tz-cart-rec-card__eyebrow', product.season ? `${product.season} // GEAR` : 'TREEZYN // GEAR'));
    const title = make(isDrawer ? 'a' : 'span', 'tz-cart-rec-card__title', product.title);
    if (isDrawer) title.href = product.url || allGearUrl;
    body.appendChild(title);

    const price = make('span', 'tz-cart-rec-card__price');
    const onSale = product.compareAt > product.price && product.price > 0;
    price.appendChild(make('span', onSale ? 'tz-cart-rec-card__price--sale' : '', money(product.price)));
    if (onSale) price.appendChild(make('span', 'tz-cart-rec-card__compare', money(product.compareAt)));
    body.appendChild(price);
    if (!isDrawer) body.appendChild(make('span', 'tz-cart-rec-card__cta', 'VIEW GEAR →'));

    card.append(media, body);
    if (isDrawer) addRecommendationControls(card, product, source, price);
    return card;
  }

  function createRecommendationSection(products, source, context) {
    const section = make('section', `tz-cart-recommendations tz-cart-recommendations--${context}`);
    section.dataset.tzCartRecommendations = context;
    section.setAttribute('aria-label', 'Recommended Treezyn gear');

    const inner = make('div', 'tz-cart-recommendations__inner');
    const head = make('div', 'tz-cart-recommendations__head');
    const headingGroup = make('div');
    const copy = recommendationCopy(source);
    headingGroup.appendChild(make('p', 'tz-cart-recommendations__eyebrow', copy.eyebrow));
    headingGroup.appendChild(make('h2', 'tz-cart-recommendations__title', copy.title));
    head.appendChild(headingGroup);
    inner.appendChild(head);

    const grid = make('div', 'tz-cart-recommendations__grid');
    const limit = context === 'drawer' ? 2 : 3;
    for (const product of products.slice(0, limit)) grid.appendChild(createRecommendationCard(product, source, context));
    inner.appendChild(grid);
    section.appendChild(inner);
    return section;
  }

  function createKeepShoppingSection(context) {
    const section = make('section', `tz-cart-recommendations tz-cart-recommendations--${context}`);
    section.dataset.tzCartRecommendations = context;
    const inner = make('div', 'tz-cart-recommendations__inner');
    const head = make('div', 'tz-cart-recommendations__head');
    const group = make('div');
    group.appendChild(make('p', 'tz-cart-recommendations__eyebrow', 'KEEP BUILDING YOUR KIT'));
    group.appendChild(make('h2', 'tz-cart-recommendations__title', 'NEED SOMETHING ELSE?'));
    head.appendChild(group);
    inner.appendChild(head);
    const link = make('a', 'tz-cart-keep-shopping', 'SHOP ALL GEAR →');
    link.href = state.data?.allProductsUrl || allGearUrl;
    inner.appendChild(link);
    section.appendChild(inner);
    return section;
  }

  function removeRecommendationSections() {
    document.querySelectorAll('[data-tz-cart-recommendations]').forEach((node) => node.remove());
  }

  function placeRecommendationSections(products, source, cart) {
    if (state.adding) return;
    removeRecommendationSections();
    if (!cart?.item_count) return;

    const drawer = document.querySelector('cart-drawer');
    const drawerItems = drawer?.querySelector('cart-drawer-items');
    if (drawerItems) {
      const section = products.length
        ? createRecommendationSection(products, source, 'drawer')
        : createKeepShoppingSection('drawer');
      drawerItems.insertAdjacentElement('afterend', section);
    }

    const cartMain = document.querySelector('main[data-template="cart"]');
    const cartFooter = cartMain?.querySelector('#main-cart-footer');
    if (cartFooter) {
      const footerSection = cartFooter.closest('.shopify-section') || cartFooter;
      const section = products.length
        ? createRecommendationSection(products, source, 'page')
        : createKeepShoppingSection('page');
      footerSection.insertAdjacentElement('beforebegin', section);
    }
  }

  function ensureKicker(heading, text) {
    if (!heading) return;
    const parent = heading.parentElement;
    if (!parent) return;
    let kicker = parent.querySelector(':scope > .tz-cart-kicker');
    if (!kicker) {
      kicker = make('p', 'tz-cart-kicker');
      parent.insertBefore(kicker, heading);
    }
    kicker.textContent = text;
  }

  function ensureTrustRail(target, position = 'beforebegin') {
    if (!target) return;
    const parent = target.parentElement;
    if (!parent || parent.querySelector(':scope > .tz-cart-trust')) return;

    const rail = make('div', 'tz-cart-trust');
    rail.setAttribute('aria-label', 'Checkout support');
    rail.append(
      make('span', 'tz-cart-trust__item', 'TREEZYN TECHNICAL GEAR'),
      make('span', 'tz-cart-trust__item', 'SECURE CHECKOUT')
    );
    target.insertAdjacentElement(position, rail);
  }

  function enhanceCopy(cart) {
    const count = Number(cart?.item_count || 0);
    const drawer = document.querySelector('cart-drawer');
    if (drawer) {
      const heading = drawer.querySelector('.drawer__heading');
      if (heading) {
        heading.textContent = 'YOUR GEAR.';
        ensureKicker(heading, `CART // ${count} ${count === 1 ? 'ITEM' : 'ITEMS'}`);
      }
      const empty = drawer.querySelector('.cart__empty-text');
      if (empty) empty.textContent = 'YOUR CART IS EMPTY.';
      ensureTrustRail(drawer.querySelector('.cart__ctas'));
    }

    const cartMain = document.querySelector('main[data-template="cart"]');
    if (cartMain) {
      const heading = cartMain.querySelector('.title-wrapper-with-link .title');
      if (heading) {
        heading.textContent = 'YOUR GEAR.';
        ensureKicker(heading, `CART // REVIEW // ${count} ${count === 1 ? 'ITEM' : 'ITEMS'}`);
      }
      const empty = cartMain.querySelector('.cart__empty-text');
      if (empty) empty.textContent = 'YOUR CART IS EMPTY.';
      ensureTrustRail(cartMain.querySelector('#checkout'));
    }
  }

  async function updateRecommendations(cart) {
    if (!cart?.item_count) {
      removeRecommendationSections();
      return;
    }

    if (needsDataRefresh(cart)) await refreshStructuredData();

    let products = structuredRecommendations(cart);
    let source = 'structured';
    if (!products.length) {
      const fallback = await fallbackRecommendations(cart);
      products = fallback.products;
      source = fallback.source;
    }

    placeRecommendationSections(products, source, cart);
  }

  function sync(source = 'initial') {
    window.clearTimeout(state.syncTimer);
    if (state.adding) return Promise.resolve(state.cart);
    if (state.syncPromise) { state.syncAgain = true; return state.syncPromise; }
    state.syncPromise = (async () => {
    let cart;
    do {
      state.syncAgain = false;
      cart = await fetchCart();
      if (window.TreezynCartRewards) cart = await window.TreezynCartRewards.sync(cart, state.data?.rewards);
      state.cart = cart;
      enhanceCopy(cart);
      await updateRecommendations(cart);
      dispatch('treezyn:cart:updated', {
        source,
        itemCount: cart.item_count,
        totalPrice: cart.total_price,
      });
    } while (state.syncAgain && !state.adding);
    return cart;
    })().catch((error) => {
      console.debug('[Treezyn cart] enhancement sync unavailable');
      enhanceCopy(state.cart || { item_count: 0 });
      return null;
    }).finally(() => { state.syncPromise = null; });
    return state.syncPromise;
  }

  function scheduleSync(source = 'update') {
    window.clearTimeout(state.syncTimer);
    state.syncTimer = window.setTimeout(() => sync(source), 90);
  }

  function bindEvents() {
    if (state.bound) return;
    state.bound = true;

    document.addEventListener('click', (event) => {
      const recommendation = event.target.closest('[data-tz-cart-recommendation]');
      if (recommendation && event.target.closest('a')) {
        dispatch('treezyn:cart:recommendation', {
          productId: recommendation.dataset.productId,
          source: recommendation.dataset.source,
          context: recommendation.dataset.context,
          url: event.target.closest('a').href,
        });
      }

      const checkout = event.target.closest('[name="checkout"], #checkout');
      if (checkout) {
        dispatch('treezyn:cart:checkout', {
          itemCount: state.cart?.item_count || 0,
          totalPrice: state.cart?.total_price || 0,
        });
      }
    });

    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.cartUpdate) {
      subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
        scheduleSync(event?.source || 'dawn-cart-update');
      });
    }

    document.addEventListener('shopify:section:load', () => scheduleSync('theme-editor'));
    document.addEventListener('treezyn:cart:rewards-retry', () => scheduleSync('rewards-retry'));
    window.addEventListener('pageshow', (event) => {
      if (!event.persisted) return;
      checkoutPending = false;
      document.querySelectorAll('[data-tz-checkout-label]').forEach((button) => {
        button.textContent = button.dataset.tzCheckoutLabel;
        button.disabled = false;
      });
      scheduleSync('history');
    });
    document.addEventListener('visibilitychange', () => { if (!document.hidden) scheduleSync('visible'); });
    // Recheck gifts before normal cart checkout, including an Enter-key submit.
    let checkoutPending = false;
    document.addEventListener('submit', async (event) => {
      const button = event.submitter;
      if (!button?.matches('[name="checkout"], #checkout') || !window.TreezynCartRewards) return;
      event.preventDefault();
      if (state.adding || checkoutPending) return;
      checkoutPending = true;
      const label = button.textContent;
      button.dataset.tzCheckoutLabel = label;
      button.textContent = 'Checking cart…';
      button.disabled = true;
      const cart = await sync('checkout');
      if (cart && window.TreezynCartRewards.canCheckout(cart)) {
        window.location.assign(`${root}checkout`);
        return;
      }
      const current = document.querySelector(button.id ? `#${button.id}` : '[name="checkout"]');
      if (current) { current.textContent = label; current.disabled = false; }
      checkoutPending = false;
    }, true);
  }

  function init() {
    state.data = readDataFromNode();
    bindEvents();
    enhanceCopy({ item_count: Number(document.getElementById('TZCartData')?.dataset.itemCount || 0) });
    sync('initial');
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();



